// Smart Daara - Bulk CSV Student Import Modal
// Flow: pick file -> preview validation -> confirm import -> report + download generated passwords.

import React, { useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { userAPI } from '../../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable CSV import dialog (students OR teachers).
 * Props:
 *  - open (bool)
 *  - onOpenChange (fn)
 *  - mode: 'student' (default) | 'teacher'
 *  - onImported (fn) — called with the report after a successful commit
 */
export default function StudentImportDialog({ open, onOpenChange, onImported, mode = 'student' }) {
  const { language } = useLanguage();
  const t = (fr, en) => (language === 'fr' ? fr : en);
  const isTeacherMode = mode === 'teacher';

  const apiDownloadTemplate = isTeacherMode ? userAPI.downloadTeacherImportTemplate : userAPI.downloadImportTemplate;
  const apiPreview = isTeacherMode ? userAPI.previewTeacherImport : userAPI.previewStudentImport;
  const apiCommit = isTeacherMode ? userAPI.commitTeacherImport : userAPI.commitStudentImport;
  const templateFileName = isTeacherMode ? 'import_enseignants_template.csv' : 'import_eleves_template.csv';
  const labelPlural = isTeacherMode ? t('enseignants', 'teachers') : t('élèves', 'students');
  const labelSingular = isTeacherMode ? t('enseignant', 'teacher') : t('élève', 'student');

  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setReport(null);
    setPreviewing(false);
    setCommitting(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await apiDownloadTemplate();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = templateFileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('Impossible de télécharger le template', 'Cannot download template'));
    }
  };

  const handleFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setReport(null);
    setPreviewing(true);
    try {
      const res = await apiPreview(f);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Erreur d\'analyse du CSV', 'CSV parsing error'));
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } finally {
      setPreviewing(false);
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const res = await apiCommit(file);
      setReport(res.data);
      const c = res.data.created_count;
      const e = res.data.error_count;
      if (c > 0) toast.success(t(`${c} ${labelSingular}(s) importé(s)`, `${c} ${labelSingular}(s) imported`));
      if (e > 0) toast.warning(t(`${e} ligne(s) en erreur`, `${e} row(s) with errors`));
      if (onImported) onImported(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Erreur lors de l\'import', 'Import error'));
    } finally {
      setCommitting(false);
    }
  };

  const downloadPasswords = () => {
    if (!report?.created?.length) return;
    const rows = report.created
      .filter((c) => c.password_generated && c.password)
      .map((c) => [c.email, c.first_name, c.last_name, c.password].map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','));
    if (rows.length === 0) {
      toast.info(t('Aucun mot de passe généré à télécharger', 'No generated passwords to download'));
      return;
    }
    const csv = 'email,first_name,last_name,password\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mots_de_passe_generes.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${mode}-import-modal`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            {t(`Importer des ${labelPlural} depuis un CSV`, `Import ${labelPlural} from CSV`)}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Téléchargez le template, remplissez-le, puis importez le fichier. Les mots de passe vides seront générés automatiquement.",
              'Download the template, fill it in, then upload. Empty passwords are auto-generated.'
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: pick file */}
        {!report && (
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('Télécharger le template CSV', 'Download CSV template')}
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFilePick}
                className="hidden"
                data-testid="csv-file-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="pick-csv-btn"
                disabled={previewing}
              >
                {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {file ? file.name : t('Choisir un fichier CSV…', 'Pick a CSV file…')}
              </Button>
              {file && (
                <Button variant="ghost" size="sm" onClick={reset}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  {t('Réinitialiser', 'Reset')}
                </Button>
              )}
            </div>

            {preview && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-4 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600">{t('Lignes totales', 'Total rows')} :</span>
                    <Badge>{preview.total_rows}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-700 font-medium">{preview.valid_rows}</span>
                    <span className="text-slate-600">{t('valides', 'valid')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-red-700 font-medium">{preview.invalid_rows}</span>
                    <span className="text-slate-600">{t('à corriger', 'to fix')}</span>
                  </div>
                </div>

                <div className="max-h-72 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-100">
                      <tr className="text-left">
                        <th className="py-2 px-2">#</th>
                        <th className="py-2 px-2">{isTeacherMode ? t('Enseignant', 'Teacher') : t('Élève', 'Student')}</th>
                        <th className="py-2 px-2">Email</th>
                        <th className="py-2 px-2">{isTeacherMode ? t('Spécialité', 'Specialization') : t('Classe', 'Class')}</th>
                        <th className="py-2 px-2">{t('Statut', 'Status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r) => (
                        <tr key={r.row_number} className={r.valid ? 'border-b' : 'bg-red-50 border-b border-red-100'}>
                          <td className="py-1.5 px-2 text-slate-500">{r.row_number}</td>
                          <td className="py-1.5 px-2">{r.first_name} {r.last_name}</td>
                          <td className="py-1.5 px-2 font-mono text-xs">{r.email}</td>
                          <td className="py-1.5 px-2">
                            {isTeacherMode ? (
                              r.specialization ? (
                                <Badge variant="outline" className="text-emerald-700">{r.specialization}</Badge>
                              ) : <span className="text-slate-400">—</span>
                            ) : (
                              r.resolved_class_id ? (
                                <Badge variant="outline" className="text-emerald-700">{r.class_name}</Badge>
                              ) : r.class_name ? (
                                <Badge variant="outline" className="text-red-600">{r.class_name} ?</Badge>
                              ) : <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-1.5 px-2">
                            {r.valid ? (
                              <span className="text-emerald-700 inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> OK
                              </span>
                            ) : (
                              <span className="text-red-700 text-xs" title={r.errors.join(', ')}>
                                {r.errors.join(', ')}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {preview.total_rows > preview.rows.length && (
                  <p className="text-xs text-slate-500">
                    {t(
                      `Aperçu limité aux ${preview.rows.length} premières lignes.`,
                      `Preview limited to first ${preview.rows.length} rows.`
                    )}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2: report */}
        {report && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">{t('Créés', 'Created')}</span>
                </div>
                <p className="text-3xl font-bold text-emerald-800 mt-2" data-testid="import-created-count">
                  {report.created_count}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <div className="flex items-center gap-2 text-red-700">
                  <XCircle className="w-5 h-5" />
                  <span className="font-semibold">{t('Erreurs', 'Errors')}</span>
                </div>
                <p className="text-3xl font-bold text-red-800 mt-2">{report.error_count}</p>
              </div>
            </div>

            {report.created.some((c) => c.password_generated) && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-between gap-3">
                <p className="text-sm text-amber-800">
                  {t(
                    "Des mots de passe ont été générés automatiquement. Téléchargez-les maintenant — ils ne seront plus visibles.",
                    'Some passwords were auto-generated. Download them now — they will not be shown again.'
                  )}
                </p>
                <Button
                  onClick={downloadPasswords}
                  className="bg-amber-600 hover:bg-amber-700 flex-shrink-0"
                  data-testid="download-passwords-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('Télécharger', 'Download')}
                </Button>
              </div>
            )}

            {report.errors.length > 0 && (
              <div className="max-h-56 overflow-y-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-100">
                    <tr className="text-left">
                      <th className="py-2 px-2">{t('Ligne', 'Row')}</th>
                      <th className="py-2 px-2">Email</th>
                      <th className="py-2 px-2">{t('Raison', 'Reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.errors.map((e, i) => (
                      <tr key={i} className="border-b bg-red-50">
                        <td className="py-1.5 px-2 text-slate-600">{e.row}</td>
                        <td className="py-1.5 px-2 font-mono">{e.email}</td>
                        <td className="py-1.5 px-2 text-red-700">{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!report ? (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={committing}>
                {t('Annuler', 'Cancel')}
              </Button>
              <Button
                onClick={handleCommit}
                disabled={!preview || preview.valid_rows === 0 || committing}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="confirm-import-btn"
              >
                {committing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t(
                  `Confirmer l'import (${preview?.valid_rows || 0} ${labelSingular}(s))`,
                  `Confirm import (${preview?.valid_rows || 0} ${labelSingular}(s))`
                )}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleClose(false)}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="close-import-btn"
            >
              {t('Fermer', 'Close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
